import { isNil, isNotNil } from "@etudescarre/utils";
import { type CSSProperties, useLayoutEffect, useState } from "react";

// Two-row table headers with row headers spanning several rows (904, 905),
// optionally under a sticky table title (910 contracts panel).
// Safari only honours `position: sticky` on table cells, not on <thead>/<tr>:
// pin every header cell. The offsets come from the measured title and header
// heights (CSS vars set on a common ancestor), not from hard-coded values:
// Safari sizes table cells differently, which left a strip between the two
// sticky rows where scrolled text showed through. The second row pins exactly
// where it sits at rest, so it doesn't move on the first scroll.
// `--sticky-title` is only set by the hook that measures a title, so a table
// nested under another section's sticky title inherits that offset.
export const stickyTitleClass = "sticky top-0 z-30";
export const stickyHeadFirstRowClass =
  "sticky top-[var(--sticky-title,0px)] z-20";
// First row of a table whose rows are split by a collapsed border (GroupedTable).
// That border stays with the table when the cells pin, so scrolled text showed
// through the strip it left: the cell paints the line itself (`after`), and the
// second row, painted later, hides what runs over.
// At a fractional zoom a pinned cell also sits between two device pixels, and
// its antialiased edges let through what scrolls beneath, top and bottom. So
// both pseudo-elements start inside the cell, in its background colour: their
// own edges land on something opaque.
export const stickyHeadRuledFirstRowClass = `${stickyHeadFirstRowClass} before:absolute before:inset-x-0 before:-top-px before:h-1 before:bg-gray-100 after:absolute after:inset-x-0 after:top-[calc(100%-2px)] after:h-1.5 after:border-t-2 after:border-gray-100 after:bg-gray-200`;
export const stickyHeadSecondRowClass =
  "sticky top-[calc(var(--sticky-title,0px)+var(--sticky-head-1,40px))] z-20";
// Content of a cell spanning several rows: stays pinned under the header while
// those rows scroll, and leaves with its last row.
export const stickyRowHeaderContentClass =
  "sticky top-[calc(var(--sticky-title,0px)+var(--sticky-head-total,80px))]";
// Label of a building header in a lots × zones grid (910, "Travaux et zoning"):
// pinned just right of the w-56 row-header column and to the right edge, so a
// building wider than the screen keeps its name in view while any of its zones
// is visible. Not on the cell itself, and no `truncate` there: its overflow
// hidden would make the cell the label's scroll container.
export const stickyBuildingLabelClass =
  "sticky right-2 left-58 inline-block max-w-full truncate align-top";

type StickyOffsets = { title: number | null; first: number; total: number };

/**
 * Measures the title and the two header rows so the sticky offsets match what
 * the browser actually rendered. Callback refs: a title or table mounted after
 * the first render (empty state, then a first row) is measured too.
 */
export function useStickyHeaderOffsets() {
  const [title, setTitle] = useState<HTMLElement | null>(null);
  const [thead, setThead] = useState<HTMLTableSectionElement | null>(null);
  const [offsets, setOffsets] = useState<StickyOffsets>({
    title: null,
    first: 40,
    total: 80,
  });

  useLayoutEffect(() => {
    if (isNil(title) && isNil(thead)) return;
    const measure = () => {
      setOffsets({
        title: title?.getBoundingClientRect().height ?? null,
        first: thead?.rows.item(0)?.getBoundingClientRect().height ?? 40,
        total: thead?.getBoundingClientRect().height ?? 80,
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (isNotNil(title)) observer.observe(title);
    if (isNotNil(thead)) observer.observe(thead);
    return () => observer.disconnect();
  }, [title, thead]);

  const style: CSSProperties & Record<`--${string}`, string> = {
    ...(isNotNil(offsets.title) && { "--sticky-title": `${offsets.title}px` }),
    "--sticky-head-1": `${offsets.first}px`,
    "--sticky-head-total": `${offsets.total}px`,
  };

  return { titleRef: setTitle, headerRef: setThead, style };
}

/**
 * Blank height after the last section of a scroll area, so that section can
 * reach the top and its sticky title replace the one above, however short it
 * is. None when everything fits: nothing to scroll for.
 */
export const scrollEndGap = ({
  contentHeight,
  viewHeight,
  sectionHeight,
}: {
  contentHeight: number;
  viewHeight: number;
  sectionHeight: number;
}) =>
  contentHeight > viewHeight ? Math.max(0, viewHeight - sectionHeight) : 0;

/**
 * Ref for an empty element closing the last section of a scroll area (a direct
 * child of it), sized to `scrollEndGap`. `contentKey` changes when a sibling
 * of the section appears or goes.
 */
export function useScrollEndSpacer(contentKey: unknown) {
  const [spacer, setSpacer] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const section = spacer?.parentElement;
    const scroller = section?.parentElement;
    if (isNil(spacer) || isNil(section) || isNil(scroller)) return;
    const fit = () => {
      const gap = spacer.offsetHeight;
      const height = scrollEndGap({
        contentHeight: scroller.scrollHeight - gap,
        viewHeight: scroller.clientHeight,
        sectionHeight: section.offsetHeight - gap,
      });
      spacer.style.height = `${height}px`;
    };
    const observer = new ResizeObserver(fit);
    observer.observe(scroller);
    for (const child of scroller.children) observer.observe(child);
    return () => observer.disconnect();
  }, [spacer, contentKey]);

  return setSpacer;
}
